<template>
  <v-card class="overflow-hidden live-tracking-sidebar px-2">
    <v-menu offset-y>
      <template v-slot:activator="{ on, attrs, value }">
        <span
          class="black--text d-flex align-center w-100 border-bottom live-tracking-sidebar-menu"
          v-bind="attrs"
          v-on="on"
        >
          <span class="font-14 font-weight-bold">{{
            trackingEngineerStatus?.text
          }}</span>
          <v-avatar
            class="mx-2"
            size="23"
            :color="trackingEngineerStatus?.color"
          >
            <span class="white--text font-weight-medium">{{
              trackingEngineerStatus?.count
            }}</span>
          </v-avatar>
          <v-icon v-if="value">mdi-chevron-up</v-icon>
          <v-icon v-else>mdi-chevron-down</v-icon>
        </span>
      </template>
      <v-list tile>
        <v-list-item
          v-for="(row, index) in trackingEngineerStatusList"
          :key="`engineer-status-list-${index}`"
          @click="updateEngineerStatus(row)"
          link
        >
          <v-list-item-title class="font-14">
            {{ row.text }}
          </v-list-item-title>
          <v-list-item-avatar class="my-0">
            <v-avatar size="23" :color="row.color">
              <span class="white--text font-weight-medium">{{
                row.count
              }}</span>
            </v-avatar>
          </v-list-item-avatar>
        </v-list-item>
      </v-list>
    </v-menu>
    <v-sheet height="calc(100vh - 111px)" class="overflow-y-auto">
      <v-text-field
        dense
        filled
        solo
        flat
        color="cyan"
        hide-details
        v-model="search"
        clearable
        placeholder="Search Cleaner..."
        append-icon="mdi-magnify"
        @input="filterEngineer"
        class="my-3"
        :loading="trackingEngineerLoading"
      />
      <div>
        <div
          v-if="trackingEngineerLoading"
          class="d-flex justify-center align-center my-4"
        >
          <v-progress-circular indeterminate color="cyan" />
        </div>
        <div v-else>
          <v-list
            tile
            style="
              max-height: calc(100vh - 168px);
              overflow-y: scroll;
              padding: 0;
            "
          >
            <v-list-item
              v-for="(row, index) in trackingEngineers"
              :key="`live-tracking-engineers-${index}`"
              class="border-top"
              :class="{
                'active-card': row?.uuid === activeEngineerUuid,
              }"
              @click="$emit('hoverEngineerMarker', row)"
            >
              <v-list-item-avatar>
                <v-img :src="row?.profile_logo ?? $defaultProfileImage" />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ row.display_name }}
                  <template v-if="!row.is_tracking_enabled">
                    <v-tooltip right content-class="custom-right-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          color="red"
                          class="ml-1"
                        >
                          mdi-map-marker-remove
                        </v-icon>
                      </template>
                      Location tracking is disabled for this technician.
                    </v-tooltip>
                  </template>
                  <template v-else>
                    <v-tooltip top content-class="custom-top-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          class="ml-1"
                          :color="row.is_online ? 'green' : 'red'"
                        >
                          mdi-access-point
                        </v-icon>
                      </template>

                      {{
                        row.last_online_at
                          ? "Last online at " +
                            formatDateTime(row.last_online_at)
                          : "Cleaner has not been online yet"
                      }}
                    </v-tooltip>
                    <v-tooltip top content-class="custom-top-tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          class="ml-1"
                          :color="row.is_location_enabled ? 'green' : 'red'"
                        >
                          {{
                            row.is_location_enabled
                              ? "mdi-map-marker"
                              : "mdi-map-marker-off"
                          }}
                        </v-icon>
                      </template>
                      {{
                        row.is_location_enabled
                          ? "Cleaner has granted GPS permission."
                          : "Cleaner has not granted GPS permission."
                      }}
                    </v-tooltip>
                  </template>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ row?.designation }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-avatar size="20"
                ><v-avatar size="20" class="white--text" color="blue">{{
                  row?.total_visits
                }}</v-avatar>
              </v-list-item-avatar>
            </v-list-item>
          </v-list>
        </div>
      </div>
    </v-sheet>
  </v-card>
</template>
<script>
import { head } from "lodash";
import { QUERY, GET } from "@/core/services/store/request.module";
import CommonMixin from "@/core/plugins/common-mixin";

const engineerStatusList = [
  {
    text: "All Cleaner",
    value: "all",
    count: 0,
    color: "cyan",
  },
  {
    text: "Active Cleaner",
    value: "active",
    count: 0,
    color: "green",
  },
  {
    text: "In-Active Cleaner",
    value: "in-active",
    count: 0,
    color: "red",
  },
];

export default {
  name: "live-tracking-engineer-sidebar",
  mixins: [CommonMixin],
  props: {
    activeEngineerUuid: {
      type: String,
      default: null,
    },
    date: {
      type: String,
      default: null,
    },
    onlineStatus: {
      type: Object,
      default: null,
    },
  },
  watch: {
    date(newValue) {
      if (newValue) {
        this.filterEngineer();
      }
    },
    onlineStatus: {
      deep: true,
      immediate: true,
      handler(newValue) {
        if (newValue?.engineerUuid) {
          for (let i = 0; i < this.trackingEngineers.length; i++) {
            const technician = this.trackingEngineers[i];
            if (technician?.uuid === newValue?.engineerUuid) {
              this.trackingEngineers[i].is_online = newValue?.status ?? false;
              break;
            }
          }
        }
      },
    },
  },
  data() {
    return {
      search: null,
      timeout: null,
      timeoutDuration: 500,
      trackingEngineerStatusList: engineerStatusList,
      trackingEngineerStatus: head(engineerStatusList),
      trackingEngineers: [],
      trackingEngineerLoading: false,
    };
  },
  methods: {
    filterEngineer() {
      clearTimeout(this.timeout);
      this.trackingEngineerLoading = true;
      this.trackingEngineers = [];
      this.timeout = setTimeout(() => {
        this.getEngineers({
          date: this.date,
          search: this.search,
        });
      }, this.timeoutDuration);
    },
    updateEngineerStatus(row) {
      this.trackingEngineerStatus = row;
      this.filterEngineer();
    },
    getEngineers(query) {
      this.$store
        .dispatch(QUERY, {
          url: "tracking/engineers",
          data: {
            ...query,
            status: this.trackingEngineerStatus?.value ?? "all",
          },
        })
        .then((response) => {
          this.trackingEngineers = response?.data ?? [];
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.trackingEngineerLoading = false;
        });
    },
    getEngineerStatus() {
      this.$store
        .dispatch(GET, {
          url: "tracking/engineers/status",
        })
        .then((response) => {
          this.trackingEngineerStatusList = response?.data ?? [];
          this.trackingEngineerStatus = head(this.trackingEngineerStatusList);
        })
        .catch((error) => {
          this.logError(error);
        });
    },
  },
  mounted() {
    this.filterEngineer();
    this.getEngineerStatus();
  },
};
</script>
